// ============================================================================
// File: src/elements/hudReel/hudReelAutoplay.ts
// (Your file, updated with the end-of-reel UX hook.)
// ============================================================================
import { log } from "../../utils/debug";
import { forceMute, forceUnmute, pauseAllOtherMedia } from "../../utils/mediaUtils";
import { AudioController } from "@/utils/audioController";
import { MediaUnlock } from "../../utils/mediaUnlock";
import { attachReelArrowBridge } from "./reelArrowBridge"; // bridge reels to existing arrow UI

function emitMuteChange(muted: boolean, section: HTMLElement, video: HTMLVideoElement) {
  try { section.setAttribute("data-muted", muted ? "1" : "0"); } catch {}
  try {
    document.dispatchEvent(
      new CustomEvent("sc:mutechange", { detail: { muted, section, video } })
    );
  } catch {}
}

const RAMP_STEP = 0.2;
const RAMP_INTERVAL_MS = 40;
const RAMP_DELAY_MS = 120;

export type AutoplayResult =
  | "played-unmuted"
  | "played-muted-then-unmuted"
  | "played-muted"
  | "failed";

function markGlobalAudioUnlocked() {
  try {
    (window as any).__storycore_audio_played = {
      ...(window as any).__storycore_audio_played,
      reel: true,
    };
    (window as any).__storycore_audio_unlocked = true;
  } catch {}
  try {
    const mu: any = MediaUnlock as any;
    const mark = mu?.markAsUnlocked ?? mu?.markUnlocked ?? mu?.setUnlocked;
    if (typeof mark === "function") {
      mark("reel-autoplay");
    }
  } catch {}
  try { AudioController.setAudioAllowed(true); } catch {}
}

async function autoplayWithFallback(
  video: HTMLVideoElement,
  preferUnmuted: boolean,
  section: HTMLElement
): Promise<AutoplayResult> {
  const globallyMuted = (() => {
    try {
      return AudioController.isMuted();
    } catch {
      return false;
    }
  })();

  // If globally muted, always keep reels muted and do not toggle global state.
  if (globallyMuted) {
    try {
      video.muted = true;
      try { video.setAttribute("muted", ""); } catch {}
      forceMute(video);
      const p = video.play();
      if (p && typeof (p as any).then === "function") {
        await p;
      }
      emitMuteChange(true, section, video);
      return "played-muted";
    } catch {
      return "failed";
    }
  }

  const tryPlay = async () => {
    const p = video.play();
    if (p && typeof (p as any).then === "function") {
      await p;
    }
  };

  const rampUnmute = () => {
    try {
      try { video.muted = false; } catch {}
      try { video.removeAttribute("muted"); } catch {}
      forceUnmute(video);
      emitMuteChange(false, section, video);

      // Smooth volume ramp using requestAnimationFrame
      const startTime = performance.now();
      const initialVolume = 0;
      const targetVolume = 1;
      const duration = 300;
      function step(now: number) {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        try { video.volume = initialVolume + (targetVolume - initialVolume) * progress; } catch {}
        if (progress < 1 && !video.paused) {
          requestAnimationFrame(step);
        } else {
          try { video.volume = targetVolume; } catch {}
        }
      }
      try { video.volume = 0; } catch {}
      requestAnimationFrame(step);

      // Abort the "pause" fallback if the clip actually finishes.
      video.addEventListener(
        "pause",
        (ev) => {
          // Why: avoid flipping global mute for natural end/user pause.
          const dur = video.duration || 0;
          const atEnd = video.ended || (dur > 0 && video.currentTime >= dur - 0.25);
          const userPaused = (ev as Event & { isTrusted?: boolean })?.isTrusted === true;

          if (atEnd || userPaused) return;

          try { video.muted = true; video.setAttribute("muted", ""); } catch {}
          forceMute(video);
          video.play().catch(() => {});
          emitMuteChange(true, section, video);
        },
        { once: true } as AddEventListenerOptions
      );

      video.addEventListener(
        "ended",
        () => { /* no-op, abort logic removed */ },
        { once: true }
      );
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.debug("[Autoplay] rampUnmute noop:", err);
      }
    }
  };

  if (preferUnmuted) {
    try {
      video.muted = false;
      try { video.removeAttribute("muted"); } catch {}
      forceUnmute(video);
      await tryPlay();
      emitMuteChange(false, section, video);
      markGlobalAudioUnlocked();
      return "played-unmuted";
    } catch {
      try {
        video.muted = true;
        try { video.setAttribute("muted", ""); } catch {}
        forceMute(video);
        emitMuteChange(true, section, video);
        await tryPlay();
        setTimeout(rampUnmute, RAMP_DELAY_MS);
        return "played-muted-then-unmuted";
      } catch {
        return "failed";
      }
    }
  }

  try {
    video.muted = true;
    try { video.setAttribute("muted", ""); } catch {}
    forceMute(video);
    await tryPlay();
    emitMuteChange(true, section, video);
    return "played-muted";
  } catch {
    return "failed";
  }
}

export async function initAutoplay(
  video: HTMLVideoElement,
  section: HTMLElement,
  isFirstReel?: boolean
) {
  try {
    (video as any).playsInline = true;
    video.setAttribute("playsinline", "");
  } catch {}

  try {
    video.autoplay = true;
    video.setAttribute("autoplay", "");
  } catch {}

  // Bridge reels to the existing arrow UI (no overlay/countdown)
  try {
    attachReelArrowBridge(video, section, { delayMs: 3000, hideOnReplay: true });
  } catch {}

  const audioAllowed = AudioController.isAudioPlayable();
  const userActivated = () => {
    try { return (window as any).__STORYCORE_USER_ACTIVATED === true; } catch { return false; }
  };
  const allowFromIntent = audioAllowed === true || userActivated();
  const mediaUnlocked = (() => {
    try {
      return MediaUnlock.isUnlocked;
    } catch {
      return false;
    }
  })();
  const hasAudioPermission = (() => {
    try { return AudioController.isAudioAllowed(); } catch { /* ignore */ }
    return false;
  })() || mediaUnlocked || allowFromIntent;

  // Mark global user intent when the first reel actually plays (manual gesture path).
  video.addEventListener("play", async () => {
    const allowMix = section.dataset.allowAudioMix === "1";
    const hasNarration =
      section.dataset.hasNarration === "true" ||
      section.dataset.hasNarration === "1";
    if (!allowMix && !hasNarration) {
      try {
        // Ensure no other media (including narration) keeps playing under this reel.
        pauseAllOtherMedia(video);
      } catch {}
    }

    if (isFirstReel) {
      try { (window as any).__STORYCORE_USER_ACTIVATED = true; } catch {}
      try { await MediaUnlock.unlockNow("reel-gate"); } catch {}
      try { await AudioController.unlockAudio("reel-play-button"); } catch {}
      try { AudioController.setAudioAllowed(true); } catch {}
    }
  }, { once: true });

  // Hard-stop autoplay for the first reel; rely on user gesture via HUD.
  const gateFirstReel = isFirstReel && !hasAudioPermission;
  if (gateFirstReel) {
    try { video.pause(); } catch {}
    try { video.currentTime = 0; } catch {}
    video.autoplay = false;
    try { video.removeAttribute("autoplay"); } catch {}
    video.muted = true;
    try { video.setAttribute("muted", ""); } catch {}
    return;
  }

  if (audioAllowed === null && !allowFromIntent) {
    if (!isFirstReel) {
      try {
        video.muted = true;
        video.setAttribute("muted", "");
        forceMute(video);
        await video.play();
        emitMuteChange(true, section, video);
      } catch (err) {
        console.warn("[Autoplay] Muted autoplay failed before first gesture", err);
      }
      return;
    }
  }

  if (audioAllowed === false && !allowFromIntent) {
    // Returning user explicitly opted out of audio → keep reels muted but playing
    try {
      video.muted = true;
      video.setAttribute("muted", "");
      forceMute(video);
      await video.play();
      emitMuteChange(true, section, video);
    } catch (err) {
      console.warn("[Autoplay] Muted autoplay failed for opted-out user", err);
    }
    return;
  }

  if (allowFromIntent) {
    // If the document hasn't been unlocked by a real gesture yet, we must still
    // obey autoplay policy: start muted, and let the HUD/play button act as gate.
    if (!mediaUnlocked) {
      try {
        video.muted = true;
        video.setAttribute("muted", "");
        forceMute(video);
        await video.play();
        emitMuteChange(true, section, video);
      } catch (err) {
        console.warn("[Autoplay] Muted autoplay failed for pre-authorized audio user", err);
      }
      return;
    }

    // 🔒 Belt-and-suspenders: mark first reel as explicitly user-allowed so guards won't re-mute it
    try { if (isFirstReel) { (video as any).dataset.userAllowed = "1"; } } catch {}

    try {
      log("[REEL-AUTO] Attempting autoplay", {
        section: section.id,
        isFirstReel,
        unlocked: AudioController.isUnlocked(),
        allowed: AudioController.isAudioAllowed(),
        userActivated: (window as any).__STORYCORE_USER_ACTIVATED,
        readyState: video.readyState,
        paused: video.paused,
        ended: video.ended,
      });

      // Attempt autoplay (unmuted) — if it fails, fallback to a click-unlock
      const res = await autoplayWithFallback(video, true, section);
      if (res === "played-unmuted" || res === "played-muted-then-unmuted" || res === "played-muted") {
        log("[REEL-AUTO] SUCCESS", section.id, res);
        if (res === "played-unmuted" || res === "played-muted-then-unmuted") {
          markGlobalAudioUnlocked();
        }
      } else {
        log("[REEL-AUTO] FAILURE", section.id, res);
      }
      if (res === "failed") {
        console.warn("[Autoplay] Unmuted autoplay failed. Waiting for user gesture...");
        log("[HUD-Autoplay] Detected autoplay policy block. Awaiting user gesture to unlock audio.");

        // Fallback to muted autoplay immediately (no gesture needed)
        try {
          const mutedRes = await autoplayWithFallback(video, false, section);
          if (mutedRes === "played-muted") {
            log("[REEL-AUTO] fallback-muted success", section.id);
            // Already playing muted; allow later gesture to lift mute.
            return;
          }
        } catch { /* continue to gesture wait */ }

        // Wait for a real user gesture to retry an unmuted start
        const onGesture = async () => {
          try {
            document.removeEventListener("click", onGesture);
            log("[Autoplay] User gesture received. Retrying unmuted play...");
            await requestUnmuteFromGesture(video, section);
          } catch (err) {
            console.warn("[Autoplay] Retry from gesture failed:", err);
          }
        };
        document.addEventListener("click", onGesture, { once: true });
      }
      return;
    } catch (err) {
      console.error("[Autoplay] Unexpected error:", err);
      return;
    }
  }
}

/**
 * Called from a real user gesture (mute/unmute button).
 */
export async function requestUnmuteFromGesture(
  video: HTMLVideoElement,
  section: HTMLElement
): Promise<boolean> {
  function _emit(muted: boolean) {
    try { section.setAttribute("data-muted", muted ? "1" : "0"); } catch {}
    try {
      document.dispatchEvent(
        new CustomEvent("sc:mutechange", { detail: { muted, section, video } })
      );
    } catch {}
  }

  try {
    // Tie the WebAudio/context unlock to this trusted gesture for this document.
    try {
      await MediaUnlock.unlockNow("reel-gate");
    } catch {
      // If this fails, we still proceed; play() below will be the gesture anchor.
    }
    AudioController.setMuted(false, "reel");
    try { video.muted = false; } catch {}
    try { video.removeAttribute("muted"); } catch {}
    forceUnmute(video);
    try { video.volume = 0; } catch {}

    await video.play();

    _emit(false);

    let v = 0;
    const id = window.setInterval(() => {
      if (video.paused) {
        try { window.clearInterval(id); } catch {}
        try { video.volume = 1; } catch {}
        return;
      }
      v += RAMP_STEP;
      if (v >= 1) {
        try { video.volume = 1; } catch {}
        try { window.clearInterval(id); } catch {}
      } else {
        try { video.volume = Math.min(1, v); } catch {}
      }
    }, RAMP_INTERVAL_MS);

    return true;
  } catch (err) {
    try {
      AudioController.setMuted(true, "reel");
      try { video.muted = true; } catch {}
      try { video.setAttribute("muted", ""); } catch {}
      forceMute(video);
      video.play().catch(() => {});
      _emit(true);
    } catch {}
    return false;
  }
}
